"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.processOrderLine = exports.updateStatusAndNotify = void 0;
const helpers_1 = require("../../helpers");
const sendEmail_1 = require("../sendEmail");
const allGolfOrderLinesConfirmed_1 = require("./allGolfOrderLinesConfirmed");
const allHotelOrderLinesConfirmed_1 = require("./allHotelOrderLinesConfirmed");
const allOrderLinesCancelled_1 = require("./allOrderLinesCancelled");
const allOrderLinesConfirmed_1 = require("./allOrderLinesConfirmed");
const allTransferOrderLinesConfirmed_1 = require("./allTransferOrderLinesConfirmed");
const checkTransferLineComplete_1 = require("./checkTransferLineComplete");
const checkTransferLinesClientComplete_1 = require("./checkTransferLinesClientComplete");
const createInvoice_1 = require("./createInvoice");
const getGolfOrderLineChanges_1 = require("./getGolfOrderLineChanges");
const getLatestOrderLineVersion_1 = require("./getLatestOrderLineVersion");
const getLinkedOrderLines_1 = require("./getLinkedOrderLines");
const getOrderById_1 = require("./getOrderById");
const getOrderLineVersionsByStatus_1 = require("./getOrderLineVersionsByStatus");
const getOrderTotal_1 = require("./getOrderTotal");
const getPreviousOrderLineVersion_1 = require("./getPreviousOrderLineVersion");
const getPreviouslyConfirmedOrderLineVersion_1 = require("./getPreviouslyConfirmedOrderLineVersion");
const getTransferLines_1 = require("./getTransferLines");
const hasTransferOrderLines_1 = require("./hasTransferOrderLines");
const isTeeSheetAvailable_1 = require("./isTeeSheetAvailable");
const updateOrderLineVersion_1 = require("./updateOrderLineVersion");
const updateOrderStatus_1 = require("./updateOrderStatus");
// Configuration constants
const ORDER_CONFIG = {
    EMAIL_TEMPLATES: {
        GOLF: {
            BOOKING_REQUEST: helpers_1.EMAIL_GOLF_LINE_BOOKING_REQUEST,
            AMENDMENT_REQUEST: helpers_1.EMAIL_GOLF_LINE_AMENDMENT_REQUEST,
            BOOKING_REMINDER: helpers_1.EMAIL_GOLF_LINE_BOOKING_REQUEST_REMINDER,
            ALTERNATIVE_OFFERED: helpers_1.EMAIL_GOLF_LINE_ALTERNATIVE_OFFERED,
            REJECTED: helpers_1.EMAIL_REJECT_GOLF_LINE,
            CANCEL_AFTER_CONFIRMATION: helpers_1.EMAIL_CANCEL_GOLF_LINE_AFTER_CONFIRMATION,
            CANCEL_BEFORE_CONFIRMATION: helpers_1.EMAIL_CANCEL_GOLF_LINE_BEFORE_CONFIRMATION,
            PURCHASE_ORDER: helpers_1.GOLF_PURCHASE_ORDER_SUPPLIER,
            REVISED_PURCHASE_ORDER: helpers_1.REVISED_GOLF_PURCHASE_ORDER_SUPPLIER,
        },
        TRANSFER: {
            BOOKING_REQUEST: helpers_1.EMAIL_TRANSFER_LINE_BOOKING_REQUEST,
            CANCEL_CONFIRMATION: helpers_1.EMAIL_CANCEL_TRANSFER_LINE_CONFIRMATION,
            CLIENT_DETAILS_REQUEST: helpers_1.EMAIL_CLIENT_TRANSFER_DETAILS_REQUEST,
            PURCHASE_ORDER: helpers_1.TRANSFER_PURCHASE_ORDER_SUPPLIER,
            REVISED_PURCHASE_ORDER: helpers_1.REVISED_TRANSFER_PURCHASE_ORDER_SUPPLIER,
        },
        HOTEL: {
            BOOKING_REQUEST: helpers_1.EMAIL_HOTEL_LINE_BOOKING_REQUEST,
        },
        ORDER: {
            PAYMENT_REQUEST: helpers_1.EMAIL_STANDARD_PAYMENT_REQUEST,
            WHOLE_ORDER_CANCELLED: helpers_1.EMAIL_WHOLE_ORDER_CANCELLED,
            SUPPORT_ORDER_CANCELLED: helpers_1.SUPPORT_EMAIL_WHOLE_ORDER_CANCELLED,
        },
    },
    STATUS_GROUPS: {
        WAITING_FOR_SUPPLIER: [
            helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER,
            helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER_OVERDUE,
        ],
        CONFIRMED: [helpers_1.OrderLineStatus.CONFIRMED, helpers_1.OrderLineStatus.CONFIRMED_PAID],
        CANCELLED: [
            helpers_1.OrderLineStatus.CANCELLED,
            helpers_1.OrderLineStatus.CANCELLED_UNAVAILABLE,
            helpers_1.OrderLineStatus.CANCELLED_REJECTED_BY_CLIENT,
        ],
    },
};
// Utility functions to reduce duplication
const createEmailObject = (templateId, orderId, orderVariantId, orderLineId, orderLineVersionId, additionalData) => ({
    language_code: "en",
    template_id: templateId,
    order_id: orderId,
    ...(orderVariantId && { order_variant_id: orderVariantId }),
    ...(orderLineId && { order_line_id: orderLineId }),
    ...(orderLineVersionId && { order_line_version_id: orderLineVersionId }),
    ...(additionalData && { data: additionalData }),
});
const sendOrderLineEmail = async (templateId, order, orderLine, orderLineVersion, additionalData) => {
    const emailObj = createEmailObject(templateId, order.id, orderLine.order_variant_id, orderLineVersion?.order_line_id || orderLine.id, orderLineVersion?.id, additionalData);
    await (0, sendEmail_1.sendEmail)(emailObj);
};
// Centralized status update with optional email notification
const updateStatusAndNotify = async (supabase, orderLineVersion, newStatus, emailTemplate, order, orderLine, additionalData) => {
    await (0, updateOrderLineVersion_1.updateOrderLineVersion)(supabase, {
        ...orderLineVersion,
        status: newStatus,
    });
    if (emailTemplate && order && orderLine) {
        await sendOrderLineEmail(emailTemplate, order, orderLine, orderLineVersion, additionalData);
    }
};
exports.updateStatusAndNotify = updateStatusAndNotify;
// Status check utilities
const getStatusFlags = (order) => {
    let allow = true;
    if (!order)
        allow = false;
    if (!order.order_lines)
        allow = false;
    if (!order.default_variant_id)
        allow = false;
    if (!order.variants)
        allow = false;
    const order_lines = order?.variants?.find((variant) => variant.id === order.default_variant_id)?.order_lines;
    if (!order_lines)
        allow = false;
    if (!allow) {
        return {
            allLinesCancelled: false,
            allLinesConfirmed: false,
            allGolfConfirmed: false,
            allHotelsConfirmed: false,
            allTransferConfirmed: false,
            hasHotels: false,
        };
    }
    return {
        allLinesCancelled: (0, allOrderLinesCancelled_1.allOrderLinesCancelled)(order),
        allLinesConfirmed: (0, allOrderLinesConfirmed_1.allOrderLinesConfirmed)(order),
        allGolfConfirmed: (0, allGolfOrderLinesConfirmed_1.allGolfOrderLinesConfirmed)(order),
        allHotelsConfirmed: (0, allHotelOrderLinesConfirmed_1.allHotelOrderLinesConfirmed)(order),
        allTransferConfirmed: (0, allTransferOrderLinesConfirmed_1.allTransferOrderLinesConfirmed)(order),
        hasHotels: order_lines?.some((item) => item.type === "HOTEL") || false,
    };
};
// Helper function to check if status is in a group
const isStatusInGroup = (status, group) => group.includes(status);
// Centralized cancellation logic
const handleLineCancellation = async (supabase, order, orderLine, orderLineVersion, previousOrderLineVersion, context) => {
    const isWaitingForSupplier = Boolean(previousOrderLineVersion &&
        isStatusInGroup(previousOrderLineVersion.status, ORDER_CONFIG.STATUS_GROUPS.WAITING_FOR_SUPPLIER));
    const isConfirmed = Boolean(previousOrderLineVersion &&
        [helpers_1.OrderLineStatus.CONFIRMED].includes(previousOrderLineVersion.status));
    const order_lines = order.variants?.find((variant) => variant.id === order.default_variant_id)?.order_lines;
    // Cancel linked lines
    const linkedLines = (0, getLinkedOrderLines_1.getLinkedOrderLines)(order_lines, orderLineVersion.order_line_id);
    if (linkedLines.length > 0) {
        context.log("Cancelling linked lines", linkedLines.length);
    }
    for (const line of linkedLines) {
        if (!line.versions)
            continue;
        const latestVersion = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
        if (latestVersion) {
            context.log("Cancelling linked line", latestVersion.id);
            await (0, updateOrderLineVersion_1.updateOrderLineVersion)(supabase, {
                ...latestVersion,
                status: helpers_1.OrderLineStatus.CANCELLED,
            });
        }
    }
    // Send cancellation email based on type and previous status
    if (isWaitingForSupplier || isConfirmed) {
        const emailTemplates = {
            GOLF: isConfirmed
                ? ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.CANCEL_AFTER_CONFIRMATION
                : ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.CANCEL_BEFORE_CONFIRMATION,
            TRANSFER: ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.CANCEL_CONFIRMATION,
        };
        const template = emailTemplates[orderLine.type];
        if (template) {
            await sendOrderLineEmail(template, order, orderLine, orderLineVersion);
        }
    }
    return { isWaitingForSupplier, isConfirmed };
};
// Individual status handlers
const handlePending = async (supabase, order, orderLine, orderLineVersion, statusFlags, hasBeenPreviouslyConfirmed, previouslyConfirmedOrderLineVersion, previousOrderLineVersion, context) => {
    if (order.mode !== "AUTOMATIC")
        return;
    const handlers = {
        HOTEL: async () => {
            await (0, exports.updateStatusAndNotify)(supabase, orderLineVersion, helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER, ORDER_CONFIG.EMAIL_TEMPLATES.HOTEL.BOOKING_REQUEST, order, orderLine);
        },
        GOLF: async () => {
            if (!statusFlags.allHotelsConfirmed && statusFlags.hasHotels) {
                context.log("WAITING FOR HOTELS TO BE CONFIRMED");
                return;
            }
            const teeSheetAvailable = await (0, isTeeSheetAvailable_1.isTeeSheetAvailable)(supabase, orderLineVersion.config);
            if (teeSheetAvailable) {
                const emailTemplate = hasBeenPreviouslyConfirmed
                    ? ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.AMENDMENT_REQUEST
                    : ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.BOOKING_REQUEST;
                const additionalData = hasBeenPreviouslyConfirmed
                    ? {
                        adjustments: (0, getGolfOrderLineChanges_1.getGolfOrderLineChanges)(previouslyConfirmedOrderLineVersion?.config, orderLineVersion?.config),
                    }
                    : undefined;
                await (0, exports.updateStatusAndNotify)(supabase, orderLineVersion, helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER, emailTemplate, order, orderLine, additionalData);
            }
            else {
                await (0, updateOrderLineVersion_1.updateOrderLineVersion)(supabase, {
                    ...orderLineVersion,
                    status: helpers_1.OrderLineStatus.WAITING_FOR_TEE_SHEET,
                });
            }
        },
        TRANSFER: async () => {
            // Handle transfer cancellation if previously confirmed
            if (previousOrderLineVersion?.status === helpers_1.OrderLineStatus.CONFIRMED) {
                await sendOrderLineEmail(ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.CANCEL_CONFIRMATION, order, orderLine, orderLineVersion, { order_line_version_id: previousOrderLineVersion.id });
            }
            if (statusFlags.allGolfConfirmed) {
                await (0, updateOrderLineVersion_1.updateOrderLineVersion)(supabase, {
                    ...orderLineVersion,
                    status: helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER,
                });
            }
        },
    };
    const handler = handlers[orderLine.type];
    if (handler) {
        await handler();
    }
};
const handleWaitingForSupplierOverdue = async (order, orderLine, orderLineVersion, statusChanged, context) => {
    if (order.mode !== "AUTOMATIC" || !statusChanged)
        return;
    if (orderLine.type === "GOLF") {
        context.log("PROCESSING GOLF BOOKING REMINDER - SENDING EMAIL");
        await sendOrderLineEmail(ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.BOOKING_REMINDER, order, orderLine, orderLineVersion);
    }
};
const handleAlternativeOffered = async (order, orderLine, orderLineVersion) => {
    if (order.mode !== "AUTOMATIC")
        return;
    if (orderLine.type === "GOLF") {
        await sendOrderLineEmail(ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.ALTERNATIVE_OFFERED, order, orderLine, orderLineVersion);
    }
};
const handleWaitingForClientTransferInfo = async (supabase, order, orderLine, orderLineVersion, context) => {
    if (orderLine.type !== "TRANSFER")
        return;
    const config = orderLineVersion?.config;
    const transferLinesComplete = await (0, checkTransferLineComplete_1.checkTransferLineComplete)(config);
    if (transferLinesComplete) {
        await (0, exports.updateStatusAndNotify)(supabase, orderLineVersion, helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER, ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.BOOKING_REQUEST, order, orderLine);
    }
    else {
        context.log("Transfer lines not complete - client info still needed");
    }
};
const handleRejectedBySupplier = async (order, orderLine, orderLineVersion, context) => {
    if (orderLine.type === "GOLF") {
        context.log("PROCESSING GOLF REJECTED_BY_SUPPLIER - SENDING EMAIL");
        await sendOrderLineEmail(ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.REJECTED, order, orderLine, orderLineVersion);
    }
};
const handleCancellation = async (supabase, order, orderLine, orderLineVersion, previousOrderLineVersion, statusFlags, context) => {
    await handleLineCancellation(supabase, order, orderLine, orderLineVersion, previousOrderLineVersion, context);
    // Handle post-cancellation logic for golf lines in automatic mode
    if (orderLine.type === "GOLF" && order.mode === "AUTOMATIC") {
        if (statusFlags.allGolfConfirmed && !statusFlags.allTransferConfirmed) {
            const transfersProcessed = await processTransferLinesAfterGolfConfirmation(supabase, order.id, context);
            if (!transfersProcessed) {
                context.log("NO TRANSFER LINES FOUND - SEND PAYMENT REQUEST");
            }
        }
    }
    // Check if all lines are cancelled and handle order cancellation
    const updatedOrder = await (0, getOrderById_1.getOrderById)(supabase, order.id);
    const updatedStatusFlags = getStatusFlags(updatedOrder);
    if (updatedStatusFlags.allLinesCancelled) {
        await (0, updateOrderStatus_1.updateOrderStatus)(supabase, updatedOrder, "CANCELLED");
        // Send cancellation emails in parallel
        await Promise.all([
            (0, sendEmail_1.sendEmail)(createEmailObject(ORDER_CONFIG.EMAIL_TEMPLATES.ORDER.WHOLE_ORDER_CANCELLED, order.id)),
            (0, sendEmail_1.sendEmail)(createEmailObject(ORDER_CONFIG.EMAIL_TEMPLATES.ORDER.SUPPORT_ORDER_CANCELLED, order.id)),
        ]);
    }
};
const handleConfirmed = async (supabase, order, orderLine, orderLineVersion, statusFlags, context) => {
    if (order.mode !== "AUTOMATIC")
        return;
    context.log("all_lines_confirmed", statusFlags.allLinesConfirmed);
    if (statusFlags.allLinesConfirmed) {
        context.log("ALL LINES CONFIRMED - update order status - WAITING_FOR_PAYMENT");
        await (0, updateOrderStatus_1.updateOrderStatus)(supabase, order, "WAITING_FOR_PAYMENT");
        context.log("PROCESSING GOLF - SENDING PAYMENT REQUEST EMAIL");
        await (0, sendEmail_1.sendEmail)(createEmailObject(ORDER_CONFIG.EMAIL_TEMPLATES.ORDER.PAYMENT_REQUEST, order.id));
        context.log("Creating invoice for order", order.id);
        context.log("order total", (0, getOrderTotal_1.getOrderTotal)(order));
        await (0, createInvoice_1.createInvoice)(supabase, order.id, `Full payment for ${order.reference}`, (0, getOrderTotal_1.getOrderTotal)(order), false, helpers_1.ADMIN_USER_ID);
    }
    else {
        // Handle partial confirmation logic
        const handlers = {
            HOTEL: async () => {
                if (statusFlags.allHotelsConfirmed) {
                    const golfLinesProcessed = await processGolfLinesAfterHotelConfirmation(supabase, order.id, context);
                    if (!golfLinesProcessed) {
                        context.log("NO GOLF LINES FOUND - SEND PAYMENT REQUEST");
                    }
                }
            },
            GOLF: async () => {
                if (statusFlags.allGolfConfirmed && !statusFlags.allTransferConfirmed) {
                    const transfersProcessed = await processTransferLinesAfterGolfConfirmation(supabase, order.id, context);
                    if (!transfersProcessed) {
                        context.log("NO TRANSFER LINES FOUND - SEND PAYMENT REQUEST");
                    }
                }
            },
        };
        const handler = handlers[orderLine.type];
        if (handler) {
            await handler();
        }
    }
};
const handleConfirmedPaid = async (orderLine, orderLineVersion, order) => {
    const totalVersions = (0, getOrderLineVersionsByStatus_1.getOrderLineVersionsByStatus)(orderLine.versions, helpers_1.OrderLineStatus.CONFIRMED_PAID);
    const isRevision = totalVersions && totalVersions.length > 1;
    const templateMap = {
        GOLF: isRevision
            ? ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.REVISED_PURCHASE_ORDER
            : ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.PURCHASE_ORDER,
        TRANSFER: isRevision
            ? ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.REVISED_PURCHASE_ORDER
            : ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.PURCHASE_ORDER,
    };
    const template = templateMap[orderLine.type];
    if (template) {
        await sendOrderLineEmail(template, order, orderLine, orderLineVersion);
    }
};
// Optimized main processing function
const processOrderLine = async (supabase, orderLineVersion, orderLine, order, context) => {
    try {
        if (!orderLine.versions)
            return;
        const previousOrderLineVersion = (0, getPreviousOrderLineVersion_1.getPreviousOrderLineVersion)(orderLine.versions);
        const previouslyConfirmedOrderLineVersion = (0, getPreviouslyConfirmedOrderLineVersion_1.getPreviouslyConfirmedOrderLineVersion)(orderLine.versions);
        const hasBeenPreviouslyConfirmed = orderLine.versions.some((version) => isStatusInGroup(version.status, ORDER_CONFIG.STATUS_GROUPS.CONFIRMED));
        const statusFlags = getStatusFlags(order);
        const type = orderLine.type;
        // Determine if we should continue processing
        const statusChanged = !previousOrderLineVersion ||
            previousOrderLineVersion.status !== orderLineVersion.status;
        const shouldContinue = statusChanged ||
            ["WAITING_FOR_CLIENT_TRANSFER_INFO", "PENDING"].includes(orderLineVersion.status);
        if (!shouldContinue) {
            context.log("STATUS HAS NOT CHANGED, DO NOTHING");
            return;
        }
        if (!statusChanged) {
            context.log("CONTINUE OVERRIDE IS TRUE");
        }
        context.log(`PROCESSING - STATUS: ${orderLineVersion.status}, TYPE: ${type}, MODE: ${order.mode}`);
        context.log("ORDER_LINE_ID", orderLine.id);
        // Create a status handler map for cleaner logic
        const statusHandlers = {
            [helpers_1.OrderLineStatus.PENDING]: () => handlePending(supabase, order, orderLine, orderLineVersion, statusFlags, hasBeenPreviouslyConfirmed, previouslyConfirmedOrderLineVersion, previousOrderLineVersion, context),
            [helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER_OVERDUE]: () => handleWaitingForSupplierOverdue(order, orderLine, orderLineVersion, statusChanged, context),
            [helpers_1.OrderLineStatus.ALTERNATIVE_OFFERED]: () => handleAlternativeOffered(order, orderLine, orderLineVersion),
            [helpers_1.OrderLineStatus.WAITING_FOR_CLIENT_TRANSFER_INFO]: () => handleWaitingForClientTransferInfo(supabase, order, orderLine, orderLineVersion, context),
            [helpers_1.OrderLineStatus.REJECTED_BY_SUPPLIER]: () => handleRejectedBySupplier(order, orderLine, orderLineVersion, context),
            [helpers_1.OrderLineStatus.CANCELLED_UNAVAILABLE]: () => handleCancellation(supabase, order, orderLine, orderLineVersion, previousOrderLineVersion, statusFlags, context),
            [helpers_1.OrderLineStatus.CANCELLED_REJECTED_BY_CLIENT]: () => handleCancellation(supabase, order, orderLine, orderLineVersion, previousOrderLineVersion, statusFlags, context),
            [helpers_1.OrderLineStatus.CANCELLED]: () => handleCancellation(supabase, order, orderLine, orderLineVersion, previousOrderLineVersion, statusFlags, context),
            [helpers_1.OrderLineStatus.CONFIRMED]: () => handleConfirmed(supabase, order, orderLine, orderLineVersion, statusFlags, context),
            [helpers_1.OrderLineStatus.CONFIRMED_PAID]: () => handleConfirmedPaid(orderLine, orderLineVersion, order),
        };
        const handler = statusHandlers[orderLineVersion.status];
        if (handler) {
            await handler();
        }
        else {
            context.log(`No handler found for status: ${orderLineVersion.status}`);
        }
    }
    catch (error) {
        context.log("ERROR in processOrderLine:", error);
        throw error; // Re-throw to let the caller handle the error appropriately
    }
};
exports.processOrderLine = processOrderLine;
// Function to process transfer lines after golf confirmation
const processTransferLinesAfterGolfConfirmation = async (supabase, orderId, context) => {
    try {
        // Get the fresh order object
        const order = await (0, getOrderById_1.getOrderById)(supabase, orderId);
        if (!(0, hasTransferOrderLines_1.hasTransferOrderLines)(order)) {
            context.log("NO TRANSFER LINES FOUND - SEND PAYMENT REQUEST");
            return false;
        }
        const transferLines = (0, getTransferLines_1.getTransferLines)(order);
        context.log("TRANSFER LINES", transferLines);
        // Check if the transfer lines have their client required data set
        const transferLinesClientCompleteData = await (0, checkTransferLinesClientComplete_1.checkTransferLinesClientComplete)(supabase, transferLines);
        const transferLinesClientComplete = transferLinesClientCompleteData.all_complete;
        if (!transferLinesClientComplete &&
            transferLinesClientCompleteData.complete_lines.length > 0) {
            for (const line of transferLinesClientCompleteData.complete_lines) {
                if (!line.versions)
                    continue;
                const orderLineVersion = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
                if (!orderLineVersion)
                    continue;
                await (0, exports.updateStatusAndNotify)(supabase, orderLineVersion, helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER, ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.BOOKING_REQUEST, order, line);
            }
        }
        await (0, updateOrderStatus_1.updateOrderStatus)(supabase, order, "CONFIRMING_ALL");
        if (!transferLinesClientComplete) {
            // Send email requesting transfer details from client
            await (0, sendEmail_1.sendEmail)(createEmailObject(ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.CLIENT_DETAILS_REQUEST, order.id));
            return false; // Process stopped due to incomplete transfer lines
        }
        else {
            // Transfer lines are complete - update status and send emails to suppliers
            const emailPromises = transferLines.map(async (line) => {
                if (!line.versions)
                    return;
                const orderTransferLineVersion = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
                if (orderTransferLineVersion) {
                    await (0, updateOrderLineVersion_1.updateOrderLineVersion)(supabase, {
                        ...orderTransferLineVersion,
                        status: helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER,
                    });
                    return sendOrderLineEmail(ORDER_CONFIG.EMAIL_TEMPLATES.TRANSFER.BOOKING_REQUEST, order, line, orderTransferLineVersion);
                }
            });
            await Promise.all(emailPromises.filter(Boolean));
            return true; // Successful processing
        }
    }
    catch (error) {
        context.log("ERROR in processTransferLinesAfterGolfConfirmation:", error);
        return false; // Error during processing
    }
};
const processGolfLinesAfterHotelConfirmation = async (supabase, orderId, context) => {
    try {
        // Get the fresh order object
        const order = await (0, getOrderById_1.getOrderById)(supabase, orderId);
        if (!order?.variants) {
            return false;
        }
        const variant = order.variants.find((variant) => variant.id === order.default_variant_id);
        if (!variant?.order_lines) {
            return false;
        }
        const golfLines = variant.order_lines.filter((line) => line.type === "GOLF");
        context.log("GOLF LINES", golfLines);
        // If there are no golf lines, return false
        if (golfLines.length === 0) {
            return false;
        }
        // Check if all golf lines are already confirmed
        const allGolfConfirmed = (0, allGolfOrderLinesConfirmed_1.allGolfOrderLinesConfirmed)(order);
        if (allGolfConfirmed) {
            context.log("ALL GOLF LINES ALREADY CONFIRMED");
            return false;
        }
        await (0, updateOrderStatus_1.updateOrderStatus)(supabase, order, "CONFIRMING_GOLF");
        // Process each golf line
        const processPromises = golfLines.map(async (line) => {
            if (!line.versions)
                return;
            const orderGolfLineVersion = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
            if (!orderGolfLineVersion)
                return;
            const teeSheetAvailable = await (0, isTeeSheetAvailable_1.isTeeSheetAvailable)(supabase, orderGolfLineVersion.config);
            const newStatus = teeSheetAvailable
                ? helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER
                : helpers_1.OrderLineStatus.WAITING_FOR_TEE_SHEET;
            await (0, updateOrderLineVersion_1.updateOrderLineVersion)(supabase, {
                ...orderGolfLineVersion,
                status: newStatus,
            });
            if (teeSheetAvailable) {
                context.log("PROCESSING GOLF - SENDING EMAIL");
                return sendOrderLineEmail(ORDER_CONFIG.EMAIL_TEMPLATES.GOLF.BOOKING_REQUEST, order, line, orderGolfLineVersion);
            }
        });
        await Promise.all(processPromises.filter(Boolean));
        return true; // Successful processing of golf lines
    }
    catch (error) {
        context.log("ERROR in processGolfLinesAfterHotelConfirmation:", error);
        return false;
    }
};
